<script setup lang="ts">
import { nextTick, watch } from 'vue'

import type { FineUploaderModalUploadHelpers } from '@/composables/current-student-advanced-standing-form/fineUploaderModalUploadHelpers'
import { useExternalCourseModalController } from '@/composables/current-student-advanced-standing-form/useExternalCourseModalController'
import { maxUploadAttachments } from '@/constants/current-student-advanced-standing-form/upload'
import {
  buildExternalCourseDeleteRequest,
  buildExternalCourseEditPayload,
  externalCourseDeleteConfirmationMessage,
} from '@/models/current-student-advanced-standing-form/externalCourseModal'
import {
  clearModalAlert,
  showModalError,
  showModalInfo,
} from '@/models/current-student-advanced-standing-form/modalAlert'
import type {
  ExternalCourseLevel,
  FineUploaderUpload,
  InstitutionListQuery,
  InstitutionListResponse,
  StudentEditSubmissionRequest,
  StudentEditSubmissionResponse,
  StudentFormSubmission,
} from '@/models/current-student-advanced-standing-form/api'
import ExternalCourseModal from './ExternalCourseModal.vue'

type ConfirmDeleteAction = () => Promise<void>

const props = defineProps<{
  courseLevelList: ExternalCourseLevel[]
  secureData: string
  modalIsShown: (modalId: string) => boolean
  modalIsActive: (modalId: string) => boolean
  syncModalVisibility: (modalId: string, visible: boolean) => void
  searchInstitutions: (query: InstitutionListQuery) => Promise<InstitutionListResponse>
  editStudentSubmission: (payload: StudentEditSubmissionRequest) => Promise<StudentEditSubmissionResponse>
  setSubmission: (submission: StudentFormSubmission) => void
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
  openConfirmDelete: (message: string, action: ConfirmDeleteAction) => void
  uploadHelpers: FineUploaderModalUploadHelpers
  triggerUploadInput: (event: MouseEvent) => void
  uploadButtonKeydown: (event: KeyboardEvent) => void
  setUploadButtonHover: (event: MouseEvent, hover: boolean) => void
  setUploadButtonFocus: (event: FocusEvent, focused: boolean) => void
  cancelStagedUpload: (attachment: FineUploaderUpload) => void
  uploadThumbnailUrl: (attachment: FineUploaderUpload) => string
}>()

const {
  externalCourseModalVisible,
  externalCourseSaving,
  externalCourseModalError,
  externalCourseModalAlertTone,
  currentExternalCourse,
  externalCourseInstitution,
  externalCourseInstitutionName,
  institutionSearchText,
  externalCourseName,
  externalCourseCode,
  externalCourseLevelId,
  externalCourseCompletedYear,
  courseAttachmentDeleteIds,
  courseNewAttachments,
  courseUploadsInProgress,
  courseUploadDropActive,
  courseUploadAlertMessage,
  institutionSuggestions,
  institutionActiveIndex,
  showCourseFineUploader,
  visibleCourseNewAttachments,
  openAddExternalCourseModal,
  openEditExternalCourseModal,
  closeExternalCourseModal,
  onInstitutionSearchInput,
  selectInstitution,
  blurInstitutionSearch,
  onInstitutionSearchKeydown,
  useCustomInstitution,
  closeCourseUploadAlert,
  onCourseUploadDragEnter,
  onCourseUploadDragOver,
  onCourseUploadDragLeave,
  onCourseUploadDrop,
  onCourseAttachmentSelected,
  stageCourseAttachmentDelete,
  deleteStagedCourseAttachment,
  retryCourseAttachment,
} = useExternalCourseModalController({
  maxAttachments: maxUploadAttachments,
  searchInstitutions: props.searchInstitutions,
  uploadHelpers: props.uploadHelpers,
})

watch(externalCourseModalVisible, (visible) => props.syncModalVisibility('ExternalCourseModal', visible))

async function saveExternalCourseModal(): Promise<void> {
  if (externalCourseSaving.value) return

  if (courseUploadsInProgress.value > 0) {
    showModalError(externalCourseModalError, externalCourseModalAlertTone, 'Please wait for upload to complete')
    return
  }

  externalCourseSaving.value = true
  showModalInfo(externalCourseModalError, externalCourseModalAlertTone, 'Saving ...')

  try {
    const externalCoursePayload = buildExternalCourseEditPayload({
      existingCourse: currentExternalCourse.value,
      externalCourseInstitution: externalCourseInstitution.value,
      externalCourseInstitutionName: externalCourseInstitutionName.value,
      deleteAttachmentIds: courseAttachmentDeleteIds.value,
      newAttachments: courseNewAttachments.value,
      externalCourseName: externalCourseName.value,
      externalCourseCode: externalCourseCode.value,
      externalCourseLevelId: externalCourseLevelId.value,
      externalCourseCompletedYear: externalCourseCompletedYear.value,
    })

    const result = await props.editStudentSubmission({
      secure_data: props.secureData,
      external_course: externalCoursePayload,
    })
    props.setSubmission(result.data.submission)
    clearModalAlert(externalCourseModalError, externalCourseModalAlertTone)
    closeExternalCourseModal()
  } catch (error) {
    if (props.handleAuthSessionError(error, 'reload')) return

    showModalError(externalCourseModalError, externalCourseModalAlertTone, props.getErrorMessage(error))
    scrollToElementAfterRender('#externalCourseModalError')
  } finally {
    externalCourseSaving.value = false
  }
}

function confirmExternalCourseDelete(): void {
  const externalCourseId = currentExternalCourse.value?.external_course_id
  if (externalCourseId == null) return

  props.openConfirmDelete(externalCourseDeleteConfirmationMessage, async () => {
    const result = await props.editStudentSubmission(buildExternalCourseDeleteRequest(props.secureData, externalCourseId))
    props.setSubmission(result.data.submission)
    closeExternalCourseModal()
  })
}

function scrollToElementAfterRender(selector: string): void {
  void nextTick(() => {
    window.requestAnimationFrame(() => {
      document.querySelector(selector)?.scrollIntoView()
    })
  })
}

defineExpose({
  openAddExternalCourseModal,
  openEditExternalCourseModal,
  closeExternalCourseModal,
})
</script>

<template>
  <ExternalCourseModal
    :shown="modalIsShown('ExternalCourseModal')"
    :active="modalIsActive('ExternalCourseModal')"
    v-model:institution-search-text="institutionSearchText"
    :institution-suggestions="institutionSuggestions"
    :institution-active-index="institutionActiveIndex"
    v-model:external-course-level-id="externalCourseLevelId"
    :course-level-list="courseLevelList"
    v-model:external-course-name="externalCourseName"
    v-model:external-course-code="externalCourseCode"
    v-model:external-course-completed-year="externalCourseCompletedYear"
    :current-external-course="currentExternalCourse"
    :course-attachment-delete-ids="courseAttachmentDeleteIds"
    :show-course-fine-uploader="showCourseFineUploader"
    :visible-course-new-attachments="visibleCourseNewAttachments"
    :course-upload-drop-active="courseUploadDropActive"
    :course-upload-alert-message="courseUploadAlertMessage"
    :error-message="externalCourseModalError"
    :alert-tone="externalCourseModalAlertTone"
    :saving="externalCourseSaving"
    :upload-thumbnail-url="uploadThumbnailUrl"
    @use-custom-institution="useCustomInstitution"
    @institution-search-input="onInstitutionSearchInput"
    @institution-search-keydown="onInstitutionSearchKeydown"
    @institution-search-blur="blurInstitutionSearch"
    @activate-institution="institutionActiveIndex = $event"
    @select-institution="selectInstitution"
    @stage-course-attachment-delete="stageCourseAttachmentDelete"
    @course-upload-drag-enter="onCourseUploadDragEnter"
    @course-upload-drag-over="onCourseUploadDragOver"
    @course-upload-drag-leave="onCourseUploadDragLeave"
    @course-upload-drop="onCourseUploadDrop"
    @trigger-upload-input="triggerUploadInput"
    @upload-button-keydown="uploadButtonKeydown"
    @upload-button-hover="setUploadButtonHover"
    @upload-button-focus="setUploadButtonFocus"
    @course-attachment-selected="onCourseAttachmentSelected"
    @cancel-staged-upload="cancelStagedUpload"
    @retry-course-attachment="retryCourseAttachment"
    @delete-staged-course-attachment="deleteStagedCourseAttachment"
    @close-course-upload-alert="closeCourseUploadAlert"
    @delete="confirmExternalCourseDelete"
    @close="closeExternalCourseModal"
    @save="saveExternalCourseModal"
  />
</template>
